<script setup lang="ts">
/**
 * Booking Matcher — Profile Builder (CBM port Phase 3e).
 *
 * Port of CBM `src/frontend/src/pages/CVBuilder.vue` ("Ny profil"
 * form). Submits to `POST /api/booking/profiles` which writes the
 * structured form data to `fact_profiles` synchronously. The chunk
 * index gets updated on the next `cv_files` / `kompetence_matrix`
 * ingest run — i.e. the chat's `run_sql` tool sees the new
 * consultant immediately, the `find_profiles` semantic search picks
 * them up after the next indexer pass.
 *
 * Auth: the endpoint is gated by membership in `group:booking-editor`
 * (or the platform admin group as fallback). A 403 from the backend
 * surfaces as the inline error block; no separate auth gate in the UI.
 */
import { computed, onBeforeMount, reactive, ref } from "vue";

const props = defineProps<{ devUser: string }>();
const emit = defineEmits<{ (e: "exit"): void }>();

const EMAIL_DOMAIN = "@danofficeit.com";
const LEVELS = [
  { value: 1, label: "1 – Ekspert" },
  { value: 2, label: "2 – Erfaren" },
  { value: 3, label: "3 – Interesseret" },
];

interface SkillRow {
  name: string;
  level: number;
}
interface WorkRow {
  client: string;
  project: string;
  role: string;
  period_start: string;
  period_end: string;
  summary: string;
  skills_used: string[];
}
interface SaveResult {
  documentID: string;
  mode: "created" | "updated";
  skillCount: number;
  workCount: number;
}

const form = reactive({
  documentID: "",
  name: "",
  email: "",
  title: "",
  area: "",
  summary: "",
  skills: [] as SkillRow[],
  work_history: [] as WorkRow[],
  certifications: [] as string[],
  education: [] as string[],
  languages: [] as string[],
});

const skillVocab = ref<string[]>([]);
const editing = ref(false);
const resolvedId = ref("");
const saving = ref(false);
const error = ref("");
const result = ref<SaveResult | null>(null);

// tag-list scratch inputs
const newCert = ref("");
const newEducation = ref("");
const newLanguage = ref("");

const apiHeaders = computed(() => ({
  "Content-Type": "application/json",
  "X-Dev-User": props.devUser || "admin@local.test",
}));

onBeforeMount(async () => {
  try {
    const r = await fetch("/api/booking/skill-vocab", {
      headers: apiHeaders.value,
    });
    if (r.ok) {
      const data = await r.json();
      skillVocab.value = Array.isArray(data.skills) ? data.skills : [];
    }
  } catch {
    // Non-fatal — autocomplete falls back to free-text.
  }
});

// CBM derive: @danofficeit.com → upper(localpart); else manual ID.
function deriveDocumentId(): string {
  const em = form.email.trim().toLowerCase();
  if (em.endsWith(EMAIL_DOMAIN)) {
    const local = em.split("@")[0].trim();
    if (local) return local.toUpperCase();
  }
  return form.documentID.trim().toUpperCase();
}

async function checkExisting() {
  const id = deriveDocumentId();
  resolvedId.value = id;
  if (!id) {
    editing.value = false;
    return;
  }
  try {
    const r = await fetch(`/api/booking/profiles/${encodeURIComponent(id)}`, {
      headers: apiHeaders.value,
    });
    if (!r.ok) {
      editing.value = false;
      return;
    }
    const existing = await r.json();
    editing.value = true;
    form.name = form.name || existing.display_name || "";
    form.title = existing.title || form.title;
    form.area = existing.area || form.area;
    form.summary = existing.summary || form.summary;
    // skills{} → row array
    const sk = existing.skills || {};
    form.skills = Object.entries(sk).map(([name, level]) => ({
      name,
      level: typeof level === "number" ? level : 3,
    }));
    // Only `source=cv` work entries are editable here; statusmail-
    // sourced entries are preserved server-side on the next ingest
    // pass (the backend keeps them on the fact_profiles row's
    // work_history jsonb under `source: "statusmail"`).
    form.work_history = (existing.work_history || [])
      .filter((w: { source?: string }) => !w.source || w.source === "cv")
      .map((w: Partial<WorkRow>) => ({
        client: w.client || "",
        project: w.project || "",
        role: w.role || "",
        period_start: w.period_start || "",
        period_end: w.period_end || "",
        summary: w.summary || "",
        skills_used: w.skills_used || [],
      }));
    form.certifications = existing.certifications || [];
    form.education = existing.education || [];
    form.languages = existing.languages || [];
  } catch {
    // Network glitch — keep editing=false so the form treats it as
    // a fresh create. The save endpoint will still upsert.
    editing.value = false;
  }
}

function addSkill() {
  form.skills.push({ name: "", level: 3 });
}
function removeSkill(i: number) {
  form.skills.splice(i, 1);
}

function addWork() {
  form.work_history.push({
    client: "",
    project: "",
    role: "",
    period_start: "",
    period_end: "",
    summary: "",
    skills_used: [],
  });
}
function removeWork(i: number) {
  form.work_history.splice(i, 1);
}
function setWorkSkills(entry: WorkRow, value: string) {
  entry.skills_used = value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function addTag(target: "certifications" | "education" | "languages") {
  const scratchRef =
    target === "certifications"
      ? newCert
      : target === "education"
        ? newEducation
        : newLanguage;
  const v = scratchRef.value.trim();
  if (!v) return;
  form[target].push(v);
  scratchRef.value = "";
}
function removeTag(
  target: "certifications" | "education" | "languages",
  i: number,
) {
  form[target].splice(i, 1);
}

async function submit() {
  error.value = "";
  result.value = null;
  saving.value = true;
  try {
    const payload = {
      documentID: deriveDocumentId(),
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      title: form.title.trim(),
      area: form.area.trim(),
      summary: form.summary.trim(),
      skills: form.skills.filter((s) => s.name.trim()),
      work_history: form.work_history,
      certifications: form.certifications,
      education: form.education,
      languages: form.languages,
    };
    const r = await fetch("/api/booking/profiles", {
      method: "POST",
      headers: apiHeaders.value,
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const text = await r.text();
      throw new Error(text || `HTTP ${r.status}`);
    }
    result.value = (await r.json()) as SaveResult;
    resolvedId.value = result.value.documentID;
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <main class="builder">
    <header class="builder__head">
      <h1>Ny profil <span v-if="editing">(redigér)</span></h1>
      <button type="button" class="builder__back" @click="emit('exit')">
        ← Tilbage
      </button>
    </header>

    <p class="builder__note">
      Opret eller redigér en konsulent-profil i Booking Matcher. Data gemmes med
      the same in fact_profiles and is available to chat tools (run_sql)
      immediately. Search via find_profiles updates on the next indexing run.
    </p>

    <form class="builder__form" @submit.prevent="submit">
      <section class="builder__section">
        <h2>Identitet</h2>
        <div class="builder__grid">
          <label>
            Email
            <input
              v-model="form.email"
              type="email"
              placeholder="alice@danofficeit.com"
              @blur="checkExisting"
            />
            <small v-if="resolvedId"
              >documentID: <code>{{ resolvedId }}</code></small
            >
          </label>
          <label>
            Manuel ID (kun for non-DOIT-email)
            <input
              v-model="form.documentID"
              type="text"
              placeholder="fx EXT-ALICE"
              @blur="checkExisting"
            />
          </label>
          <label>
            Navn *
            <input v-model="form.name" type="text" required />
          </label>
          <label>
            Titel
            <input
              v-model="form.title"
              type="text"
              placeholder="fx Senior Citrix Engineer"
            />
          </label>
          <label>
            Area
            <input
              v-model="form.area"
              type="text"
              placeholder="fx Cloud, Identity, Data"
            />
          </label>
        </div>
        <label class="builder__textarea">
          Sammenfatning
          <textarea v-model="form.summary" rows="3" />
        </label>
      </section>

      <section class="builder__section">
        <header class="builder__row-head">
          <h2>Kompetencer</h2>
          <button type="button" class="builder__add" @click="addSkill">
            + Add skill
          </button>
        </header>
        <datalist id="skill-vocab">
          <option v-for="s in skillVocab" :key="s" :value="s" />
        </datalist>
        <div
          v-for="(skill, i) in form.skills"
          :key="i"
          class="builder__skill-row"
        >
          <input
            v-model="skill.name"
            list="skill-vocab"
            type="text"
            placeholder="fx Azure, Citrix, PowerShell"
          />
          <select v-model.number="skill.level">
            <option v-for="l in LEVELS" :key="l.value" :value="l.value">
              {{ l.label }}
            </option>
          </select>
          <button type="button" class="builder__remove" @click="removeSkill(i)">
            ×
          </button>
        </div>
        <p v-if="form.skills.length === 0" class="builder__empty">
          Ingen kompetencer endnu.
        </p>
      </section>

      <section class="builder__section">
        <header class="builder__row-head">
          <h2>Erfaring</h2>
          <button type="button" class="builder__add" @click="addWork">
            + Add experience
          </button>
        </header>
        <div v-for="(w, i) in form.work_history" :key="i" class="builder__work">
          <div class="builder__grid">
            <label>Kunde<input v-model="w.client" type="text" /></label>
            <label>Projekt<input v-model="w.project" type="text" /></label>
            <label>Rolle<input v-model="w.role" type="text" /></label>
            <label>Fra<input v-model="w.period_start" type="date" /></label>
            <label>Til<input v-model="w.period_end" type="date" /></label>
            <label
              >Skills (komma-separeret)<input
                :value="w.skills_used.join(', ')"
                type="text"
                @input="
                  setWorkSkills(w, ($event.target as HTMLInputElement).value)
                "
            /></label>
          </div>
          <label class="builder__textarea">
            Beskrivelse
            <textarea v-model="w.summary" rows="2" />
          </label>
          <button
            type="button"
            class="builder__remove builder__remove--row"
            @click="removeWork(i)"
          >
            Slet erfaring
          </button>
        </div>
        <p v-if="form.work_history.length === 0" class="builder__empty">
          Ingen erfaringer endnu.
        </p>
      </section>

      <section class="builder__section builder__taglists">
        <div>
          <h3>Certificeringer</h3>
          <div class="builder__tag-input">
            <input
              v-model="newCert"
              type="text"
              placeholder="fx MS Azure Solutions Architect"
              @keyup.enter.prevent="addTag('certifications')"
            />
            <button type="button" @click="addTag('certifications')">+</button>
          </div>
          <ul>
            <li v-for="(t, i) in form.certifications" :key="t">
              {{ t }}
              <button type="button" @click="removeTag('certifications', i)">
                ×
              </button>
            </li>
          </ul>
        </div>
        <div>
          <h3>Uddannelse</h3>
          <div class="builder__tag-input">
            <input
              v-model="newEducation"
              type="text"
              placeholder="fx MSc Computer Science, AU"
              @keyup.enter.prevent="addTag('education')"
            />
            <button type="button" @click="addTag('education')">+</button>
          </div>
          <ul>
            <li v-for="(t, i) in form.education" :key="t">
              {{ t }}
              <button type="button" @click="removeTag('education', i)">
                ×
              </button>
            </li>
          </ul>
        </div>
        <div>
          <h3>Sprog</h3>
          <div class="builder__tag-input">
            <input
              v-model="newLanguage"
              type="text"
              placeholder="fx Dansk, Engelsk"
              @keyup.enter.prevent="addTag('languages')"
            />
            <button type="button" @click="addTag('languages')">+</button>
          </div>
          <ul>
            <li v-for="(t, i) in form.languages" :key="t">
              {{ t }}
              <button type="button" @click="removeTag('languages', i)">
                ×
              </button>
            </li>
          </ul>
        </div>
      </section>

      <footer class="builder__foot">
        <button type="submit" class="builder__submit" :disabled="saving">
          {{ saving ? "Gemmer…" : editing ? "Opdatér profil" : "Opret profil" }}
        </button>
        <p v-if="result" class="builder__success">
          Profil {{ result.documentID }}
          {{ result.mode === "created" ? "oprettet" : "opdateret" }} ({{
            result.skillCount
          }}
          kompetencer, {{ result.workCount }} erfaringer).
        </p>
        <p v-if="error" class="builder__error">{{ error }}</p>
      </footer>
    </form>
  </main>
</template>

<style scoped>
.builder {
  max-width: 880px;
  margin: 0 auto;
  padding: var(--space-4) var(--space-3) var(--space-6);
}
.builder__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: var(--space-2);
}
.builder__head h1 {
  margin: 0;
  color: var(--color-ink);
}
.builder__head h1 span {
  font-weight: 400;
  color: var(--color-muted);
  font-size: 0.85em;
}
.builder__back {
  background: none;
  border: 0;
  color: var(--color-accent);
  cursor: pointer;
}
.builder__note {
  color: var(--color-muted);
  font-size: 0.88rem;
  margin: 0 0 var(--space-4);
  line-height: 1.5;
}
.builder__form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.builder__section {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3);
}
.builder__section h2 {
  margin: 0 0 var(--space-3);
  font-size: 1rem;
  color: var(--color-ink);
}
.builder__section h3 {
  margin: 0 0 var(--space-2);
  font-size: 0.85rem;
  color: var(--color-ink);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.builder__row-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-2);
}
.builder__row-head h2 {
  margin: 0;
}
.builder__add,
.builder__remove,
.builder__submit {
  cursor: pointer;
  font-family: inherit;
}
.builder__add {
  background: var(--color-accent-dim);
  color: var(--color-accent);
  border: 1px solid var(--color-accent);
  border-radius: var(--radius-sm);
  padding: 0.3em 0.9em;
  font-size: 0.85rem;
}
.builder__remove {
  background: transparent;
  color: var(--color-muted);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  width: 28px;
  height: 28px;
  font-size: 1.05rem;
  line-height: 1;
}
.builder__remove--row {
  width: auto;
  height: auto;
  padding: 0.3em 0.9em;
  font-size: 0.85rem;
  margin-top: var(--space-2);
}
.builder__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-2) var(--space-3);
}
.builder__grid label,
.builder__textarea {
  display: flex;
  flex-direction: column;
  font-size: 0.8rem;
  color: var(--color-muted);
  gap: 0.25rem;
}
.builder__grid input,
.builder__grid select,
.builder__textarea textarea,
.builder__skill-row input,
.builder__skill-row select,
.builder__tag-input input {
  font-family: inherit;
  font-size: 0.9rem;
  padding: 0.45rem 0.6rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  color: var(--color-ink);
}
.builder__skill-row {
  display: grid;
  grid-template-columns: 1fr 200px 32px;
  gap: var(--space-2);
  align-items: center;
  margin-bottom: var(--space-2);
}
.builder__work {
  background: var(--color-surface-alt, var(--color-surface));
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: var(--space-2) var(--space-3);
  margin-bottom: var(--space-2);
}
.builder__taglists {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: var(--space-3);
}
.builder__tag-input {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--space-2);
}
.builder__tag-input button {
  padding: 0 0.8em;
  font-size: 1rem;
  background: var(--color-accent);
  color: white;
  border: 0;
  border-radius: var(--radius-sm);
  cursor: pointer;
}
.builder__taglists ul {
  list-style: none;
  margin: var(--space-2) 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.builder__taglists li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.2rem 0.5rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.85rem;
}
.builder__taglists li button {
  background: transparent;
  border: 0;
  color: var(--color-muted);
  cursor: pointer;
}
.builder__empty {
  color: var(--color-muted);
  font-style: italic;
  margin: 0;
  font-size: 0.9rem;
}
.builder__foot {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-top: var(--space-3);
}
.builder__submit {
  align-self: flex-start;
  background: var(--color-accent);
  color: white;
  border: 0;
  border-radius: var(--radius-sm);
  padding: 0.6em 1.5em;
  font-size: 0.95rem;
  font-weight: 600;
}
.builder__submit:disabled {
  background: var(--color-muted);
  cursor: not-allowed;
}
.builder__success {
  color: var(--color-success);
  margin: 0;
}
.builder__error {
  color: var(--color-error, #b91c1c);
  margin: 0;
  font-size: 0.9rem;
  white-space: pre-wrap;
}
</style>
